import { createApp, defineAsyncComponent } from 'vue';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { Disposable } from '@patsnap/tahiti-core';
import type { OnboardingActionGuideRuntime } from '../onboarding-action-guide-runtime';
import '@/presentation/components/onboarding/onboarding-action-guide.scss';

/** 激活按钮引导基础状态的样式类。 */
const TARGET_CLASS = 'onboarding-action-guide-target';
/** 展示小手时关闭目标自身动画，仅保留手势提示。 */
const HAND_ONLY_TARGET_CLASS = 'onboarding-action-guide-target--hand-only';
/** 动态 Vue 手型组件使用的宿主节点样式类。 */
const HOST_CLASS = 'onboarding-action-guide-host';
/** 按需加载的通用点击手势组件。 */
const OnboardingClickGesture = defineAsyncComponent(
    () => import('../../components/OnboardingClickGesture.vue'),
);

/**
 * 通用按钮操作引导的浏览器运行时。
 *
 * Vue 子应用和 DOM 操作都封装在此处，上层 service 不依赖具体挂载技术。
 */
@injectable()
export class OnboardingActionGuideRuntimeImpl implements OnboardingActionGuideRuntime {
    /**
     * 在目标元素内部创建独立 Vue 子应用并挂载点击手型。
     * @param target 手型需要跟随的目标元素。
     * @returns 可幂等卸载 Vue 子应用和宿主节点的句柄。
     */
    mountGuideHand(target: Element): Disposable {
        // 手型挂在目标内部，可直接复用目标的相对定位并始终跟随按钮移动。
        /** 承载手型 Vue 子应用的 DOM 节点。 */
        const host = document.createElement('span');
        host.className = HOST_CLASS;
        host.setAttribute('aria-hidden', 'true');
        target.appendChild(host);
        target.classList.add(HAND_ONLY_TARGET_CLASS);

        /** 循环渲染点击手势，直到上层引导结束并卸载。 */
        const app = createApp(OnboardingClickGesture, { loop: true });
        try {
            app.mount(host);
        } catch (error) {
            target.classList.remove(HAND_ONLY_TARGET_CLASS);
            host.remove();
            throw error;
        }
        /** 防止重复卸载同一个 Vue 应用。 */
        let isDisposed = false;

        return {
            dispose: () => {
                // 清理操作幂等，允许计时器、点击和页面销毁同时触发。
                if (isDisposed) return;
                isDisposed = true;
                app.unmount();
                host.remove();
                target.classList.remove(HAND_ONLY_TARGET_CLASS);
            },
        };
    }

    /**
     * 激活目标元素的引导基础状态。
     * @param target 需要展示引导的目标元素。
     */
    addTargetClass(target: Element): void {
        target.classList.add(TARGET_CLASS);
    }

    /**
     * 移除目标元素的引导基础状态。
     * @param target 需要清理引导的目标元素。
     */
    removeTargetClass(target: Element): void {
        target.classList.remove(TARGET_CLASS);
    }
}
